import { sanitizeCustomHtml } from "@redotech/order-tracking-common/sanitize-custom-html";
import { TrackingPageCustomHtmlBlock } from "@redotech/order-tracking-common/tracking-page-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { isFramedBlock } from "@redotech/redo-block-service-common/block-content";
import { renderBlockServiceHtml } from "@redotech/redo-block-service-common/liquid";
import htmlReactParser from "html-react-parser";
import { memo, RefObject, useEffect, useMemo, useRef } from "react";
import { useInRouterContext } from "react-router-dom";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import { pageRoute, useCustomHtmlContext } from "./custom-html-context";
import { CustomHtmlFrame } from "./custom-html-frame";
import { CustomHtmlIsolator } from "./custom-html-isolator";
import { localThemeVariables, useCustomHtmlTheme } from "./custom-html-theme";
import * as css from "./custom-html.module.css";
import { observeBlockContentHeight } from "./runtime-block-height";
import { clickedShipmentIndex } from "./shared/fulfillment-utils";
import { useNavigateToShipment } from "./shared/use-navigate-to-shipment";
import { useFitHeightReport } from "./use-fit-height-report";

export const CustomHtml = memo(function CustomHtml(
  props: TrackingPageCustomHtmlBlock,
) {
  const context = useRequiredContext(TrackingPageContext);
  const { environment, staticPreview } = context;
  const hasContent = props.html.trim().length > 0;
  const isBuilder = environment === TrackingPageEnvironment.BUILDER;
  const framed = isFramedBlock(props);
  const liquidContext = useCustomHtmlContext(props._id);
  const inlineRef = useRef<HTMLDivElement>(null);
  // Tracking-code pages show one shipment and have no per-shipment route.
  const shipmentLinks =
    useInRouterContext() &&
    hasContent &&
    !isBuilder &&
    !framed &&
    pageRoute(context) !== "tracking-code";
  const theme = useCustomHtmlTheme();
  const reportFitHeight = useFitHeightReport(props);
  const measureRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = measureRef.current;
    if (!reportFitHeight || !element) {
      return;
    }
    return observeBlockContentHeight(element, reportFitHeight);
  }, [reportFitHeight]);

  // Blocks rendered outside the frame (inline, or a framed block's static
  // thumbnail) use the frame's Liquid options, so a `{{ }}` token shows the same
  // value everywhere. A template error keeps the raw HTML for shoppers rather
  // than blanking the block.
  const rendered = useMemo(() => {
    try {
      return {
        html: renderBlockServiceHtml(props.html, liquidContext),
        error: null,
      };
    } catch (error) {
      return {
        html: props.html,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }, [liquidContext, props.html]);

  // Pixel-only vendors (e.g. GA4) ship no markup; the frame still must mount.
  if (framed) {
    // A static preview (e.g. a thumbnail) never handshakes with the block
    // service — it can't run merchant scripts, so it shows sanitized markup.
    if (staticPreview) {
      return hasContent ? (
        <Flex dir="column" height="full" w="full">
          {htmlReactParser(sanitizeCustomHtml(rendered.html))}
        </Flex>
      ) : null;
    }
    return <CustomHtmlFrame {...props} />;
  }

  if (!hasContent && !isBuilder) {
    return null;
  }

  const inline = hasContent && !isBuilder;
  return (
    <Flex
      align={hasContent ? undefined : "center"}
      className={inline ? css.inline : undefined}
      dir="column"
      height="full"
      justify={hasContent ? undefined : "center"}
      ref={inlineRef}
      // Page typography as variables; values come from settings, so not CSS.
      style={inline ? localThemeVariables(theme) : undefined}
      w="full"
    >
      {!hasContent ? (
        <Text fontSize="sm" textColor="secondary">
          Custom HTML
        </Text>
      ) : isBuilder ? (
        <>
          <CustomHtmlIsolator html={rendered.html} theme={theme} />
          {rendered.error ? (
            <Text fontSize="xs" textColor="error">
              {rendered.error}
            </Text>
          ) : null}
        </>
      ) : (
        <>
          {reportFitHeight ? (
            // Sized by its content, unlike the root, which fills the block.
            <div ref={measureRef}>{htmlReactParser(rendered.html)}</div>
          ) : (
            htmlReactParser(rendered.html)
          )}
          {shipmentLinks ? <ShipmentLinks containerRef={inlineRef} /> : null}
        </>
      )}
    </Flex>
  );
});

/**
 * Opens a shipment in place when a `data-redo-shipment-id` element inside the
 * block is clicked, like the items block's Track Package button; the element's
 * href stays the fallback elsewhere. Delegated so it reaches parsed markup.
 */
const ShipmentLinks = memo(function ShipmentLinks({
  containerRef,
}: {
  containerRef: RefObject<HTMLDivElement>;
}) {
  const { order } = useRequiredContext(TrackingPageContext);
  const navigateToShipment = useNavigateToShipment();
  const handleClick = useHandler((event: MouseEvent) => {
    const index = clickedShipmentIndex(event.target, order?.shipments);
    if (index === undefined) {
      return;
    }
    event.preventDefault();
    navigateToShipment(index);
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [containerRef, handleClick]);

  return null;
});
